<template>
  <span>
    <div class="md-layout buttons-header">
      <div class="md-layout-item ml-auto">
        <md-button
          @click="tryAppendNewRootNode(dialogs)"
          :disabled="isNotCheckedOut || selectNodeMode.active"
          v-scroll="onScroll"
          class="md-warning"
          style="margin-right: 12px;"
          >{{$t('dialog.addNode')}}</md-button
        >
        <md-button
          @click="tryAppendNewChild(dialogs, nodeData, nodePath)"
          :disabled="!editDialog || isNotCheckedOut || selectNodeMode.active"
          class="md-facebook"
          style="margin-right: 12px;"
          >{{$t('dialog.addChildNode')}}</md-button
        >
        <md-button
          @click="tryAppendNewFolder(dialogs)"
          :disabled="isNotCheckedOut || selectNodeMode.active"
          v-scroll="onScroll"
          class="md-github">{{$t('dialog.addFolder')}}</md-button>
      </div>
    </div>
    <div class="md-layout" style="padding-left: 20px;">
      <div class="md-layout-item">
        <TreeView
          @onNodeClick="editNode"
          @scrollNode="scrollToNode"
          @deleteDialog="openDeleteDialog"
          @duplicate="tryDuplicateNode"
          @appendNewChild="tryAppendNewChild"
          @addSiblingAbove="tryAddSiblingAbove"
          @addSiblingBellow="tryAddSiblingBellow"
          ref="treeView"
          :node="nodeData"
          :value="dialogs"
          :nodeMap="nodeMap"
          :indent="indentation"
          @drop="onDrop"
          @before-drop="onBeforeDrop"
          droppable
          :draggable="!isNotCheckedOut && !selectNodeMode.active"
          :viewingMode="viewingMode"
        ></TreeView>
      </div>
      <md-dialog-confirm
        :md-active.sync="deleteDialog"
        :md-title="$t('dialog.wantToRemoveThisNode')"
        :md-confirm-text="$t('dialog.yes')"
        :md-cancel-text="$t('dialog.no')"
        @md-cancel="deleteDialog = false"
        @md-confirm="tryDeleteNode(deleteDialogObj.value, deleteDialogObj.node, deleteDialogObj.path)"
      />
    </div>
    <aside
      :class="{
        'v-navigation-drawer--open drawerVisual': editDialog,
        'v-navigation-drawer--close': !editDialog,
      }"
      :style="{ width: editDialog ? '810px' : '0px' }"
      class="v-navigation-drawer v-navigation-drawer--absolute v-navigation-drawer--right theme--light"
      style="
        position: fixed;
        z-index: 5;
        height: 100%;
        top: 0px;
        transform: translateX(0%);
      "
      data-booted="true"
    >
      <div class="v-navigation-drawer__content">
        <EditNodeDialog
          ref="editNodeDialog"
          :node="nodeData"
          @save="saveNode"
          @scrollNode="scrollToNode"
          @closeDialog="closeDialog"
          :nodeMap="nodeMap"
          :treeData="dialogs"
          :entities="entities"
          :intents="intents"
        />
      </div>
    </aside>
  </span>
</template>

<script>
import 'he-tree-vue/dist/he-tree-vue.css'
import TreeView from '../components/TreeNode'
import { mapFields } from 'vuex-map-fields'
import createNodeMixin from './data/createNodeMixin'
import EditNodeDialog from '../components/EditNodeDialog.vue'
import Swal from 'sweetalert2'
import { convertListToTree, convertTreeToList, setPreviousSibling } from '@/helpers/dialogTree'
import * as hp from 'helper-js'

export default {
  model: {
    prop: 'dialogNodes',
    event: 'update'
  },
  components: { TreeView, EditNodeDialog },
  props: {
    dialogNodes: { required: true, type: Array },
    intents: { required: true, type: Array },
    entities: { required: true, type: Array },
    viewingMode: { required: true, type: Boolean }
  },
  computed: {
    ...mapFields('dialogEdit', [
      'editDialog',
      'nodePath',
      'typeDialog',
      'selectNodeMode'
    ]),
    isNotCheckedOut () {
      return (
        this.$store.state.snapshots.appliedSnapshot._source.status !==
          'checkedout' || this.viewingMode === true
      )
    }
  },
  mixins: [createNodeMixin],
  data () {
    return {
      offsetTop: 0,
      indentation: 40,
      dialogs: [],
      nodeMap: {},
      nodeData: {},
      deleteDialog: false,
      deleteDialogObj: {}
    }
  },
  beforeMount () {
    try {
      const { tree, nodeMap } = convertListToTree(this.dialogNodes)
      this.dialogs = tree
      this.nodeMap = nodeMap
    } catch (err) {
      let type = this.$t('dialog.internalError')
      this.errorDialog(type, err)
      throw err
    }
    // this.$on('set-node', function (e) {
    //   console.log('setNode')
    //   this.setNodeData(e)
    // })
  },
  watch: {
    nodeData (newValue, oldValue) {
      if (oldValue) {
        this.$set(oldValue, '$active', false)
      }
      if (newValue) {
        this.$set(newValue, '$active', true)
      }
      const { nodeMap } = convertListToTree(this.dialogNodes)
      this.nodeMap = nodeMap
    }
  },
  methods: {
    closeDialog () {
      this.editDialog = false
      this.nodeData = {}
    },
    async errorDialog (type, errorMsg) {
      const { value } = await Swal.fire({
        icon: 'error',
        title: type,
        html: `${this.$t('dialog.contactTheAdministrator')} <br> ${errorMsg}`
      })
      if (value) {
        this.editDialog = false
        setTimeout(async () => {
          const appliedSnapshot = this.$store.getters['snapshots/appliedSnapshot']
          const snapReq = await this.$axios.get(`/snapshots/${appliedSnapshot?._id}`)
          const { data, ...snapInfo } = snapReq.data._source
          this.$store.commit('snapshots/setAppliedSnapshot', {
            _id: snapReq.data._id,
            _source: snapInfo,
            _data: data
          })
        }, 50)
      }
    },
    async tryDeleteNode (oldTree, node, path) {
      try {
        const updatedList = await this.deleteNode(oldTree, node, path)
        const { tree, nodeMap } = convertListToTree(updatedList)
        this.dialogs = tree
        this.nodeMap = nodeMap
      } catch (err) {
        let type = this.$t('dialog.errorDeletingNode')
        this.errorDialog(type, err)
      }
    },
    async tryDuplicateNode ({ value, node, path }) {
      try {
        // console.log('tryDuplicateNode')
        // console.log({ value, node, path })
        await this.duplicateNode(value, node, path)
      } catch (err) {
        let type = this.$t('dialog.duplicateNodeError')
        this.errorDialog(type, err)
      }
    },
    async tryAppendNewChild (value, node, path) {
      try {
        // console.log('tryAppendNewChild')
        // console.log({ value, node, path })
        await this.appendNewChild(value, node, path)
      } catch (err) {
        let type = this.$t('dialog.duplicateNodeError')
        this.errorDialog(type, err)
      }
    },
    async tryAddSiblingAbove ({ value, node, path }) {
      try {
        // console.log('tryAddSiblingAbove')
        // console.log({ value, node, path })
        await this.addSiblingAbove(value, node, path)
      } catch (err) {
        let type = this.$t('dialog.errorAddingSiblingNodeAbove')
        this.errorDialog(type, err)
      }
    },
    async tryAddSiblingBellow ({ value, path }) {
      try {
        // console.log('tryAddSiblingBellow')
        // console.log({ value, path })
        await this.addSiblingBellow(value, path)
      } catch (err) {
        let type = this.$t('dialog.errorAddingSiblingNodeBelow')
        this.errorDialog(type, err)
      }
    },
    async tryAppendNewFolder (dialogs) {
      try {
        await this.appendNewFolder(dialogs)
      } catch (err) {
        let type = this.$t('dialog.errorAddingNewFolder')
        this.errorDialog(type, err)
      }
    },
    async tryAppendNewRootNode (dialogs) {
      try {
        await this.appendNewRootNode(dialogs)
      } catch (err) {
        let type = this.$t('dialog.errorAddingNode')
        this.errorDialog(type, err)
      }
    },
    editNode ({ node, path, type }) {
      this.nodeData = node
      this.nodePath = path
      this.$set(node, '$active', true)
      // this.$refs.editNodeDialog.setSelectedNode(node)
      this.$nextTick(() => {
        this.editDialog = true
      })
    },
    async saveNode ({ node }) {
      try {
        const treeHelper = new hp.TreeData(this.dialogs)
        const siblings = treeHelper.getNodeParent(this.nodePath)?.children || this.dialogs
        const nodeIndex = siblings.findIndex((child) => child.data.dialog_node === node.data.dialog_node)
        this.$set(siblings, nodeIndex, node)
        const checkedOutSnap = this.$store.getters['snapshots/checkedOutByUser']
        const dialogNodes = convertTreeToList(this.dialogs)
        await this.$store.dispatch('snapshots/save', {
          id: checkedOutSnap._id,
          data: {
            dialog_nodes: dialogNodes
          }
        })
        this.$emit('update', dialogNodes)
        this.nodeData = node
        this.editDialog = false
      } catch (err) {
        if (err.message === 'SNAPSHOT_MUST_BE_CHECKEDOUT') {
          alert(this.$t('dialog.snapshotIsNotCheckedOut'))
          this.$router.push('/snapshots')
        } else if (err.message === 'IN_VIEWING_MODE') {
          alert(this.$t('dialog.youAreInPreviewMode'))
        } else {
          let type = this.$t('dialog.errorSavingChange')
          this.errorDialog(type, err)
        }
      }
      this.$emit('savedDialogs')
    },
    appendChildNode () {
      const nodeId = this.nodeData.data.dialog_node
      document.getElementById(nodeId + 'addChildNode').click()
    },
    openDeleteDialog (e) {
      this.deleteDialog = true
      this.deleteDialogObj.value = e.value
      this.deleteDialogObj.node = e.node
      this.deleteDialogObj.path = e.path
    },
    onScroll (e) {
      this.offsetTop = e.scrollTop
    },
    setNodeData (nodeData) {
      this.nodeData = nodeData
    },
    onBeforeDrop (
      /** @type {import('he-tree-vue').Store} */
      treeStore
    ) {
      const { startPath, startTree } = treeStore
      const oldPosition = startPath[startPath.length - 1]
      const oldParent = startTree.getNodeParentByPath(startPath)
      this.oldPrevSibling = oldParent
        ? oldParent?.children?.[oldPosition - 1]
        : startTree.value[oldPosition - 1]
      this.oldNextSibling = oldParent?.children
        ? oldParent?.children?.[oldPosition + 1]
        : startTree.value[oldPosition + 1]
    },

    async onDrop (
      /** @type {import('he-tree-vue').Store} */
      treeStore
    ) {
      const { startPath, targetPath, dragNode, targetTree } = treeStore
      // Check if the node was actually moved
      for (let i; i < startPath.length; i++) {
        if (startPath[i] !== targetPath[i]) {
          // console.warn(`Drag and drop but no change in node position`)
          return
        }
      }
      const parent = targetTree.getNodeParentByPath(targetPath)
      const position = targetPath[targetPath.length - 1]

      const nextSibling = parent
        ? parent?.children?.[position + 1]
        : targetTree.value[position + 1]
      // console.log('nextSibling', nextSibling)
      const prevSibling = parent
        ? parent?.children?.[position - 1]
        : targetTree.value[position - 1]
      // console.log('prevSibling', prevSibling)
      if (this.oldNextSibling) {
        // console.log('oldNextSibling', this.oldNextSibling)
        this.oldNextSibling.data.previous_sibling =
          // eslint-disable-next-line camelcase
          this.oldPrevSibling?.data?.dialog_node
      }

      if (nextSibling) {
        nextSibling.data.previous_sibling = dragNode.data.dialog_node
      }

      // eslint-disable-next-line camelcase
      setPreviousSibling(dragNode, prevSibling, parent)

      // eslint-disable-next-line camelcase
      dragNode.data.parent = parent?.data?.dialog_node

      const checkedOutSnap = this.$store.getters['snapshots/checkedOutByUser']
      const data = {
        dialog_nodes: convertTreeToList(targetTree.value)
      }
      try {
        this.$emit('isSavingDialogs')
        await this.$store.dispatch('snapshots/save', {
          id: checkedOutSnap._id,
          data
        })
        this.$emit('update', data.dialog_nodes)
      } catch (err) {
        if (err.message === 'SNAPSHOT_MUST_BE_CHECKEDOUT') {
          alert(this.$t('dialog.snapshotIsNotInCheckedoutStatus'))
          this.$router.push('/snapshots')
        } else {
          let type = this.$t('dialog.errorSavingChange')
          this.errorDialog(type, err)
        }
      }
      this.$emit('savedDialogs')
    },
    scrollToNode (node) {
      this.nodeData = node
      this.$set(this.nodeData, '$active', true)
      this.$nextTick(() => {
        // console.log(`sroll to`, node)
        const id = node.data.dialog_node
        const yOffset = -10
        const element = document.getElementById(id)
        const y =
          element.getBoundingClientRect().top + window.pageYOffset + yOffset

        window.scrollTo({ top: y, behavior: 'smooth' })
        this.editDialog = true
      })
      // document.getElementById(node.data.dialog_node).scrollIntoView({
      //   behavior: 'smooth'
      // })
    }
  },
  beforeDestroy () {
    // console.log('destruindo')
    this.editDialog = false
  }
}
</script>

<style scoped>
.buttons-header {
  padding-bottom: 32px;
  transition: width 0.2s;
}
.fix-buttons {
  position: fixed;
  top: 0px;
}
</style>
